/**
 * TASK-522 calendar time contract:
 * - Store UTC ISO in DB / API bodies
 * - UI pickers + display use shop timezone (shop_calendar_config.timezone)
 *
 * @see documentation/product-features/TASK-522_CALENDAR_TIMEZONE_CONTRACT.md
 */
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

export const DEFAULT_SHOP_TIMEZONE = "America/Chicago";

export function shopTimezoneOrDefault(timeZone) {
  return timeZone || DEFAULT_SHOP_TIMEZONE;
}

/** Format UTC ISO for <input type="datetime-local"> in shop TZ. */
export function utcIsoToShopLocalInput(iso, timeZone) {
  if (!iso) return "";
  const d = dayjs(iso).tz(shopTimezoneOrDefault(timeZone));
  if (!d.isValid()) return "";
  return d.format("YYYY-MM-DDTHH:mm");
}

/**
 * Parse datetime-local value as shop wall clock → UTC ISO.
 * Example (America/Chicago CDT): 2026-07-31T09:00 → 2026-07-31T14:00:00.000Z
 */
export function shopLocalInputToUtcIso(value, timeZone) {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value);
  if (!match) return null;
  const [, year, month, day, hour, minute] = match;
  const parsed = dayjs.tz(
    `${year}-${month}-${day} ${hour}:${minute}:00`,
    "YYYY-MM-DD HH:mm:ss",
    shopTimezoneOrDefault(timeZone),
  );
  if (!parsed.isValid()) return null;
  return parsed.toISOString();
}

/** Display UTC ISO as shop-local time (e.g. calendar chips). */
export function formatUtcIsoAsShopLocalTime(iso, timeZone) {
  const d = dayjs(iso).tz(shopTimezoneOrDefault(timeZone));
  if (!d.isValid()) return "";
  return d.format("h:mm A");
}

/** Shop-local calendar date key for an instant (YYYY-MM-DD). */
export function shopLocalDateKeyFromIso(iso, timeZone) {
  const d = dayjs(iso).tz(shopTimezoneOrDefault(timeZone));
  if (!d.isValid()) return "";
  return d.format("YYYY-MM-DD");
}

/** Shop-local calendar date key for a Date/moment (YYYY-MM-DD). */
export function shopLocalDateKeyFromMoment(viewMoment, timeZone) {
  return dayjs(viewMoment).tz(shopTimezoneOrDefault(timeZone)).format("YYYY-MM-DD");
}

/** Shop-local "today" as YYYY-MM-DD. */
export function shopLocalTodayKey(timeZone) {
  return dayjs().tz(shopTimezoneOrDefault(timeZone)).format("YYYY-MM-DD");
}

/**
 * 42-day month grid in shop TZ (Sun-start).
 * Each cell: { dateKey, dayOfMonth, weekdayIndex, inMonth, dayjs }
 */
export function shopMonthMatrix(viewMoment, timeZone) {
  const tz = shopTimezoneOrDefault(timeZone);
  const monthStart = dayjs(viewMoment).tz(tz).startOf("month");
  const gridStart = monthStart.subtract(monthStart.day(), "day").startOf("day");
  const cells = [];
  for (let i = 0; i < 42; i += 1) {
    const cell = gridStart.add(i, "day");
    cells.push({
      dateKey: cell.format("YYYY-MM-DD"),
      dayOfMonth: cell.date(),
      weekdayIndex: cell.day(),
      inMonth: cell.month() === monthStart.month(),
      dayjs: cell,
    });
  }
  return cells;
}

/** Fetch range for the visible 6-week shop grid (UTC ISO bounds). */
export function visibleShopGridRange(viewMoment, timeZone) {
  const cells = shopMonthMatrix(viewMoment, timeZone);
  const from = cells[0].dayjs.startOf("day").toISOString();
  const to = cells[41].dayjs.endOf("day").toISOString();
  return { from, to };
}

export function shopMonthLabel(viewMoment, timeZone) {
  return dayjs(viewMoment).tz(shopTimezoneOrDefault(timeZone)).format("MMMM YYYY");
}

export function shiftShopMonth(viewMoment, deltaMonths, timeZone) {
  return dayjs(viewMoment)
    .tz(shopTimezoneOrDefault(timeZone))
    .add(deltaMonths, "month")
    .startOf("month")
    .toDate();
}

/** Default create slot: 09:00–10:00 shop time on dateKey. */
export function defaultShopSlotInputs(dateKey, timeZone) {
  const key = dateKey || shopLocalTodayKey(timeZone);
  return {
    startLocal: `${key}T09:00`,
    endLocal: `${key}T10:00`,
  };
}

/** Persist Month|Week|Day preference per user + shop (Part 7). */
export const CALENDAR_VIEWS = ["month", "week", "day"];

export function calendarViewStorageKey(userId, shopId) {
  return `autoshops.calendar.view.${userId || "anon"}.${shopId || "0"}`;
}

export function loadSavedCalendarView(userId, shopId) {
  try {
    const raw = localStorage.getItem(calendarViewStorageKey(userId, shopId));
    if (CALENDAR_VIEWS.includes(raw)) return raw;
  } catch {
    /* ignore */
  }
  return "month";
}

export function saveCalendarView(userId, shopId, view) {
  if (!CALENDAR_VIEWS.includes(view)) return;
  try {
    localStorage.setItem(calendarViewStorageKey(userId, shopId), view);
  } catch {
    /* ignore */
  }
}

/** 7 shop-local days starting Sunday of the week containing viewMoment. */
export function shopWeekDays(viewMoment, timeZone) {
  const tz = shopTimezoneOrDefault(timeZone);
  const base = dayjs(viewMoment).tz(tz).startOf("day");
  const start = base.subtract(base.day(), "day");
  const days = [];
  for (let i = 0; i < 7; i += 1) {
    const cell = start.add(i, "day");
    days.push({
      dateKey: cell.format("YYYY-MM-DD"),
      dayOfMonth: cell.date(),
      weekdayIndex: cell.day(),
      label: cell.format("ddd D"),
      dayjs: cell,
    });
  }
  return days;
}

export function shiftShopView(viewMoment, viewMode, delta, timeZone) {
  const tz = shopTimezoneOrDefault(timeZone);
  const unit = viewMode === "day" ? "day" : viewMode === "week" ? "week" : "month";
  return dayjs(viewMoment).tz(tz).add(delta, unit).startOf(unit === "month" ? "month" : "day").toDate();
}

export function shopViewLabel(viewMoment, viewMode, timeZone) {
  const tz = shopTimezoneOrDefault(timeZone);
  const d = dayjs(viewMoment).tz(tz);
  if (viewMode === "day") return d.format("dddd, MMM D, YYYY");
  if (viewMode === "week") {
    const days = shopWeekDays(viewMoment, tz);
    return `${days[0].dayjs.format("MMM D")} – ${days[6].dayjs.format("MMM D, YYYY")}`;
  }
  return d.format("MMMM YYYY");
}

/** Hour slots for week/day (shop-local wall clock labels). */
export function shopHourSlots(startHour = 6, endHour = 20) {
  const slots = [];
  for (let h = startHour; h < endHour; h += 1) {
    slots.push({
      hour: h,
      label: dayjs().hour(h).minute(0).format("h A"),
    });
  }
  return slots;
}

export function eventShopHour(iso, timeZone) {
  const d = dayjs(iso).tz(shopTimezoneOrDefault(timeZone));
  if (!d.isValid()) return null;
  return d.hour();
}
